// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState, useEffect } from 'react';
import { Image, Pressable, StyleSheet, Platform } from 'react-native';
import { Redirect, useRootNavigationState, useLocalSearchParams } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { useUser } from './user-context';
import LoginHero from '@/assets/images/login-hero.png';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useI18n } from '@/i18n';
import { AuthStatusCard } from '@/components/AuthStatusCard';
import { useAuthRedirect } from '@/app/auth/useAuthRedirect';

export default function LoginScreen() {
  const { user, setUser } = useUser();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const { debug: debugParam, reason, returnTo } = useLocalSearchParams<{ debug?: string; reason?: string; returnTo?: string }>();
  const { t } = useI18n();

  const [error, setError] = useState<string | null>(null);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const { loading, handleRedirect } = useAuthRedirect({
    user,
    setUser,
    onError: setError,
    debug,
    returnTo: typeof returnTo === 'string' ? returnTo : null,
  });

  useEffect(() => {
    if (!isNavigationReady) return;
    if (user) {
      // handled by Redirect below
      return;
    }
    if (reason === 'user-not-found') {
      setError(t('login.userNotFound'));
    }
  }, [user, isNavigationReady, reason, t]);

  return (
    <>
      {isNavigationReady && user ? <Redirect href={`/tournaments${debug ? '?debug=true' : ''}`} /> : null}
      <View style={styles.container}>
        <Image source={LoginHero} style={styles.hero} resizeMode="contain" />
        <Text style={styles.title}>{t('auth.loginTitle')}</Text>
        <LanguageSwitcher />
        <AuthStatusCard
          title={t('auth.loginCardTitle')}
          error={error}
          buttonLabel={loading ? t('auth.loggingIn') : t('auth.loginCta')}
          onPress={handleRedirect}
          disabled={loading}
        >
          {debug ? <Text style={styles.label}>{t('auth.debugLabel')}</Text> : null}
          {debug ? (
            <Text style={styles.value}>
              {user ? `${user.forename} (${user.id})` : t('auth.debugNone')}
            </Text>
          ) : null}
          <Text style={styles.label}>{t('auth.loginSubtitle')}</Text>
          <Text style={styles.label}>{t('auth.loginHint')}</Text>
        </AuthStatusCard>
      </View>
    </>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 4px 10px rgba(0,0,0,0.25)' },
  default: {
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    gap: 16,
    justifyContent: 'center',
    backgroundColor: '#0c1328',
  },
  hero: {
    width: 496,
    height: 196,
    alignSelf: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#f8fafc',
  },
  label: {
    fontSize: 14,
    fontWeight: '700',
    color: '#d5e1ff',
  },
  value: {
    fontSize: 16,
    fontWeight: '600',
    color: '#e5edff',
    marginBottom: 4,
  },
});
